import { useState } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import {
  ShieldCheck,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Users,
  Building2,
  UserCheck,
  Megaphone,
  Bot,
  DollarSign,
  BrainCircuit,
  Server,
  Mail,
  Home,
  ExternalLink,
  Briefcase,
  Menu,
  X,
  ListOrdered,
} from "lucide-react";

// ── SIDEBAR NAV ITEMS ──
const SIDEBAR_ITEMS = [
  { id: "dashboard", label: "Overview", icon: Home, path: "/superadmin" },
  { id: "organizations", label: "Organizations", icon: Building2, path: "/superadmin/organizations" },
  { id: "brms", label: "Relationship Managers", icon: Briefcase, path: "/superadmin/brms" },
  { id: "users", label: "Platform Users", icon: Users, path: "/superadmin/users" },
  { id: "clients", label: "Debtors / Clients", icon: UserCheck, path: "/superadmin/clients" },
  { id: "campaigns", label: "Campaigns", icon: Megaphone, path: "/superadmin/campaigns" },
  { id: "automations", label: "Automations", icon: Bot, path: "/superadmin/automations" },
  { id: "payments", label: "Transactions", icon: DollarSign, path: "/superadmin/payments" },
  { id: "contacts", label: "Custom Plan Leads", icon: Mail, path: "/superadmin/contacts" },
  { id: "waitlist", label: "Waitlist Leads", icon: ListOrdered, path: "/superadmin/waitlist" },
  { id: "governance", label: "Governance & CBN", icon: ShieldCheck, path: "/superadmin/governance" },
  { id: "ai", label: "AI Analytics", icon: BrainCircuit, path: "/superadmin/ai" },
  { id: "system", label: "Infrastructure Health", icon: Server, path: "/superadmin/system" },
];

export default function SuperAdminLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();

  const activeTab = location.pathname.split("/")[2] || "dashboard";

  const renderNavBody = (isCollapsedState, isMobile = false) => (
    <>
      {/* Brand Header */}
      <div
        className={`h-16 flex items-center justify-between ${
          isCollapsedState ? "justify-center px-2" : "px-4"
        } border-b border-slate-800/80`}
      >
        <div className="flex items-center gap-3 overflow-hidden min-w-0">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center shrink-0 shadow-md shadow-indigo-500/20">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          {!isCollapsedState && (
            <div className="min-w-0">
              <p className="text-sm font-black text-white tracking-tight leading-none truncate">
                Korvry Super Admin
              </p>
              <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">
                Platform Core
              </span>
            </div>
          )}
        </div>
        {isMobile && (
          <button
            onClick={() => setMobileOpen(false)}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-all"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Live System Status Pill */}
      {!isCollapsedState && (
        <div className="px-4 pt-3 pb-1">
          <div className="flex items-center justify-between px-3 py-1.5 rounded-xl bg-slate-900/90 border border-slate-800 text-[11px]">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="font-semibold text-slate-300">Cluster Online</span>
            </div>
            <span className="font-mono text-[10px] text-indigo-400 font-bold">PROD</span>
          </div>
        </div>
      )}

      {/* Nav Items */}
      <nav className="flex-1 py-3 px-2 space-y-1 overflow-y-auto">
        {SIDEBAR_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              className={`w-full flex items-center ${
                isCollapsedState ? "justify-center" : "gap-3"
              } px-3 py-2.5 rounded-xl text-xs font-bold transition-all ${
                isActive
                  ? "bg-indigo-600 text-white shadow-lg shadow-indigo-600/30"
                  : "text-slate-400 hover:text-slate-200 hover:bg-slate-900/60"
              }`}
              title={isCollapsedState ? item.label : ""}
            >
              <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-white" : "text-slate-400"}`} />
              {!isCollapsedState && <span className="truncate">{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* Bottom Actions */}
      <div className="p-3 border-t border-slate-800/80">
        <button
          type="button"
          onClick={() => {
            if (isMobile) setMobileOpen(false);
            navigate("/dashboard");
          }}
          className={`w-full flex items-center ${
            isCollapsedState ? "justify-center" : "gap-3"
          } px-3 py-2.5 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-900 transition-all border border-slate-800/60`}
          title={isCollapsedState ? "Exit Super Admin" : ""}
        >
          <LogOut className="h-4 w-4 shrink-0 text-slate-400" />
          {!isCollapsedState && <span>Exit Super Admin</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex font-sans antialiased selection:bg-indigo-500 selection:text-white">
      {/* ── MOBILE OVERLAY & DRAWER ── */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-slate-950/80 backdrop-blur-xs transition-opacity"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="relative flex w-64 max-w-[80vw] flex-col bg-slate-950 text-slate-300 shadow-2xl z-50 h-full border-r border-slate-800">
            {renderNavBody(false, true)}
          </aside>
        </div>
      )}

      {/* ── DESKTOP SIDEBAR ── */}
      <aside
        className={`hidden md:flex ${
          collapsed ? "w-20" : "w-64"
        } bg-slate-950 border-r border-slate-800/80 h-screen fixed left-0 top-0 flex-col transition-all duration-300 z-40 shadow-2xl text-slate-300`}
      >
        {/* Floating Overflow Toggle Button */}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3.5 top-5 h-7 w-7 rounded-full bg-slate-900 border border-slate-700 text-slate-300 hover:text-white hover:bg-indigo-600 hover:border-indigo-500 shadow-md flex items-center justify-center transition-all z-50 focus:outline-none"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-3.5 w-3.5" />
          ) : (
            <ChevronLeft className="h-3.5 w-3.5" />
          )}
        </button>

        {renderNavBody(collapsed, false)}
      </aside>

      {/* ── MAIN CONTENT WRAPPER ── */}
      <div
        className={`flex-1 flex flex-col min-h-screen transition-all duration-300 ml-0 ${
          collapsed ? "md:ml-20" : "md:ml-64"
        }`}
      >
        {/* Top Sticky Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30 shadow-xs">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div className="h-7 w-7 rounded-lg bg-indigo-50 border border-indigo-200 flex items-center justify-center text-indigo-700 font-black text-xs shrink-0">
              SA
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-slate-900 truncate">{user?.contactName || "Super Administrator"}</p>
              <p className="text-[10px] text-slate-500 font-mono truncate">{user?.email}</p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <span className="hidden lg:inline-block text-[11px] font-medium text-slate-500 font-mono">
              {new Date().toLocaleDateString("en-NG", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>

            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 text-xs font-bold transition-all shadow-xs"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Switch to Org Workspace</span>
              <span className="sm:hidden">Org Workspace</span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 max-w-7xl w-full mx-auto flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
